import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AxiosInstance from "../../Service/AxiosInstance"; // Update this path as needed

// Define the interface for the user details
interface UserDetails {
  userName: string;
  userImageUrl: string;
  errorMessage: string;
  customerId: string;
  planName: string;
  isTrial: boolean;
  projectCount: number;
  startDate: Date;
  maxlabelLimit: number;
  maxModelLimit: number;
}

// Define the initial state
interface UserState {
  userName: string | null;
  loading: boolean;
  error: string | null;
  subscriptionPlan: string | null; // New field for subscription plan
  subscriptionDays: number | null; // New field for days remaining in subscription
}

const initialState: UserState = {
  userName: null,
  loading: false,
  error: null,
  subscriptionPlan: null,
  subscriptionDays: null,
};

interface SubscriptionExpiryDetails {
  plan: string;
  days: number;
}

// Create an async thunk for fetching user details
export const fetchUserDetails = createAsyncThunk(
  "user/fetchDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<UserDetails>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Subscription/UserDetails`
      );
      const { userName, projectCount, customerId, planName, maxlabelLimit, maxModelLimit } = response.data;
      // console.log(response.data);
      // Store the userName in local storage
      localStorage.setItem("userName", userName);
      localStorage.setItem("customerId", customerId);
      localStorage.setItem("projectCount", projectCount.toString());
      localStorage.setItem("plan", planName);
      localStorage.setItem("maxLableLimit", maxlabelLimit.toString());
      localStorage.setItem("maxModelLimit", maxModelLimit.toString());

      return response.data;
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Create an async thunk for fetching subscription expiry
export const fetchSubscriptionExpiry = createAsyncThunk(
  "user/fetchSubscriptionExpiry",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<SubscriptionExpiryDetails>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Subscription/SubscriptionExpiry`
      );
      return response.data; // Return the plan and days details
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Create a slice
const UserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserName: (state) => {
      state.userName = null;
      localStorage.removeItem("userName"); // Clear from local storage
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userName = action.payload.userName; // Update the state with the fetched userName
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error
      })
      .addCase(fetchSubscriptionExpiry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionExpiry.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionPlan = action.payload.plan; // Store the plan
        state.subscriptionDays = action.payload.days; // Store the remaining days
      })
      .addCase(fetchSubscriptionExpiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error
      });
  },
});

// Export the actions and the reducer
export const { clearUserName } = UserSlice.actions;
export default UserSlice.reducer;
